import React, { Component, Fragment } from 'react'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import styled from 'styled-components'
import { ResponsiveContainer } from 'app/assets/styles'
import { H3, H6 } from 'app/common/typography'
import {
    CatalogAllergens,
    CatalogHeader,
    CouponModal,
    DisplayableProductTile,
    ImpersonalOperationCard
} from 'app/ecommerce/common/components'
import { breakpoints, media } from 'app/assets/styles/breakpoints'
import { CartFooter, Layout } from 'app/ecommerce/common'
import type { EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import type {
    Category,
    DisplayableProduct
} from 'shared/store/ecommerce/catalog'
import type { ItemInfos, PurchasableItem } from 'shared/store/ecommerce/order'
import type {
    Coupon,
    FilterShortDTO,
    MetaShortDTO,
    RestaurantShortDTO
} from 'shared/types'
import {
    COUPON_CODES,
    featureCodes,
    orderPickUpType,
    restaurantServices
} from 'shared/utils'
import { AlertModal } from 'app/common/modals/alert'
import { getService } from 'shared/utils/ecommerce/utils'
import { NextPromotionScreenHandler } from '../../components/discounts/NextPromotionScreenHandler'
import { formatTableOrderValuesToPickUpInfo } from 'shared/utils-v2'
import { SPACING } from 'app-v2/design-tokens'
import { FreeHotDrinkModal } from './components'
import { FeatureToggle } from '../../../../common/feature-toggle'
import {
    BodyText,
    CaptionText,
    Spacer
} from '../../../../../app-v2/design-systems'

const { tertiary } = oldColors
const {
    CLICK_AND_COLLECT_ON_SITE,
    SERVICE_TABLE,
    TABLE_ORDER
} = restaurantServices

type Props = {
    authenticated: boolean,
    catalog: any,
    restaurant: RestaurantShortDTO,
    fetchCatalog: string => void,
    priceTotal: number,
    restaurantPageUrl?: string,
    collectModePageUrl?: string,
    collectMode?: string,
    setCurrentSuggestions: (suggestions: Array<PurchasableItem>) => void,
    withoutBacon: boolean,
    selectedAllergens: Array<string>,
    allergens: Array<FilterShortDTO>,
    resetCurrentItem: () => void,
    setCurrentItemInfos: (productInfos: ItemInfos) => void,
    fetchPointsPageData: () => void,
    fetchPrivilegesData: () => void,
    toggleWithoutBacon: () => void,
    selectAllergens: (Array<string>) => void,
    fetchAvailableFeatures: () => void,
    fetchRestaurant: (pathUrl: string) => string,
    isRestaurantAvailable: (restaurantId: string) => boolean,
    setCollectMode: (collectMode: string) => void,
    sendMeta: EventMetadata => void,
    pageMetadata: (payload: MetaShortDTO) => void,
    removeCurrentOrderToken: () => void,
    navigate: (to: string) => void,
    coupon: ?Coupon,
    intl: IntlShape,
    tablePageUrl?: string,
    terracePageUrl?: string,
    tokenPageUrl?: string,
    composable?: boolean,
    isCartToastVisible: boolean,
    idsCouponsTemporaryBurned: string[],
    setCartToast: (isCartToastVisible: boolean) => void,
    products: Array<PurchasableItem>,
    availableFeatures: string[]
}

type State = {
    width: number,
    currentCategoryId?: string,
    showCouponModal: boolean,
    catalogError: boolean,
    displayFreeHotDrinkModal: boolean
}

class Catalog extends Component<Props, State> {
    categoryRefs: { [string]: any }
    subCategoriesRefs: { [string]: any }

    constructor() {
        super()
        this.state = {
            width: window.innerWidth,
            showCouponModal: false,
            catalogError: false,
            displayFreeHotDrinkModal: false
        }
        this.categoryRefs = {}
        this.subCategoriesRefs = {}
    }

    toggleCouponModal = (value: boolean) =>
        this.setState({ showCouponModal: value })

    init = async () => {
        const {
            authenticated,
            fetchPointsPageData,
            fetchPrivilegesData,
            fetchAvailableFeatures,
            isRestaurantAvailable,
            restaurant,
            collectMode,
            restaurantPageUrl,
            collectModePageUrl,
            fetchRestaurant,
            setCurrentSuggestions,
            setCollectMode,
            pageMetadata,
            navigate,
            intl,
            removeCurrentOrderToken,
            tablePageUrl,
            terracePageUrl,
            tokenPageUrl
        } = this.props
        const { id: restaurantId } = restaurant
        let restaurantSet = restaurantId
        let collectModeIsSet = !!collectMode
        if (restaurantPageUrl) {
            restaurantSet = await fetchRestaurant(restaurantPageUrl)
        }

        const available =
            restaurantSet && (await isRestaurantAvailable(restaurantSet))
        if (!available) {
            return navigate(
                intl.formatMessage({
                    id: 'routes.clickandcollect.restaurant.choice'
                })
            )
        }
        removeCurrentOrderToken()
        if (collectModePageUrl) {
            const collectMode = getService(collectModePageUrl)
            const { services } = restaurant
            if (!services) {
                this.init()
                return
            }

            if (
                !services.includes(collectMode) ||
                (collectMode === SERVICE_TABLE &&
                    !services.includes(CLICK_AND_COLLECT_ON_SITE))
            ) {
                collectModeIsSet = false
            } else {
                collectModeIsSet = await setCollectMode(
                    collectModePageUrl,
                    formatTableOrderValuesToPickUpInfo(
                        tablePageUrl,
                        !!terracePageUrl && terracePageUrl === 'true',
                        tokenPageUrl
                    )
                )
            }
        }
        if (!collectModeIsSet) {
            return navigate(
                intl.formatMessage({
                    id: 'routes.clickandcollect.delivery.choice'
                })
            )
        }
        setCurrentSuggestions([])
        fetchAvailableFeatures()
        await this.fetchCatalog(restaurantSet)
        if (authenticated) {
            fetchPrivilegesData()
            fetchPointsPageData()
        }
        pageMetadata({})
        window.addEventListener('resize', this.updateWindowDimensions)
        window.addEventListener('scroll', this.updateScrollPosition)
        if (this.props.coupon) {
            this.toggleCouponModal(true)
        }
    }

    async componentDidMount() {
        this.init()
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions)
        window.removeEventListener('scroll', this.updateScrollPosition)
    }

    displayFreeHotDrinkModal = () => {
        this.setState({
            ...this.state,
            displayFreeHotDrinkModal: true
        })
    }

    componentDidUpdate() {
        const {
            isCartToastVisible,
            collectMode,
            idsCouponsTemporaryBurned,
            setCartToast,
            products,
            availableFeatures
        } = this.props

        if (
            !!availableFeatures &&
            availableFeatures.includes(
                featureCodes.TABLE_ORDER_FREE_HOT_DRINK // TODO: replace this one with madeleine or add one with madeleine
            ) &&
            isCartToastVisible &&
            !!products &&
            products.length === 1 &&
            !!idsCouponsTemporaryBurned &&
            !idsCouponsTemporaryBurned.includes(COUPON_CODES.FREE_HOT_DRINK) &&
            !idsCouponsTemporaryBurned.includes(COUPON_CODES.FREE_PRODUCT) && // TODO: replace this one with madeleine
            collectMode === TABLE_ORDER
        ) {
            setCartToast(false)
            this.displayFreeHotDrinkModal()
        }
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    updateScrollPosition = () => {
        const { currentCategoryId } = this.state
        if (this.categoryRefs && (!currentCategoryId || window.scrollY)) {
            const keys = Object.keys(this.categoryRefs)
            const values = Object.values(this.categoryRefs)

            const index = values.findIndex(ref => {
                const refPosition = ref && this.getRefPosition(ref)
                return window.scrollY < refPosition
            })

            const currentCategory =
                index === 0
                    ? undefined
                    : index > 0
                    ? keys[index - 1]
                    : keys[keys.length - 1]
            if (currentCategory !== currentCategoryId) {
                this.setState({ currentCategoryId: currentCategory })
            }
        }
    }

    fetchCatalog = async (restaurantId: string) => {
        const { fetchCatalog } = this.props
        try {
            await fetchCatalog(restaurantId)
        } catch (error) {
            this.setState({ catalogError: true })
        }
    }

    retryFetchCatalog = async (restaurantId: string) => {
        this.setState({ catalogError: false })
        await this.fetchCatalog(restaurantId)
    }

    renderSection = (item: Category, index: number) => {
        const { displayableProducts, id, name, subCategories } = item
        const ref = React.createRef()
        this.categoryRefs[id] = ref
        return (
            <div ref={ref} key={index}>
                <CategoryTitle>
                    <H3>{name}</H3>
                </CategoryTitle>
                {subCategories && this.renderSubCategories(subCategories)}
                {displayableProducts &&
                    this.renderTilesRow(displayableProducts)}
            </div>
        )
    }

    renderSubCategories = subCategories => {
        return (
            <Fragment>
                {this.renderSubCategoriesTab(subCategories)}
                {subCategories.map((subCategory, index) => {
                    const {
                        categoryId,
                        displayableProducts,
                        id,
                        name
                    } = subCategory
                    const ref = React.createRef()
                    this.subCategoriesRefs[categoryId + id] = ref
                    return (
                        <div ref={ref} key={index}>
                            <SubCategoryTitle>{name}</SubCategoryTitle>
                            {displayableProducts &&
                                this.renderTilesRow(displayableProducts)}
                        </div>
                    )
                })}
            </Fragment>
        )
    }

    renderSubCategoriesTab = subCategories => (
        <SubCategoriesTabs>
            <Row>
                {subCategories.map((subCategory, index) => {
                    const { categoryId, id, name } = subCategory
                    return (
                        <SubCategoryTab
                            key={index}
                            onClick={() =>
                                this.onSubCategoryTabClick({
                                    id: categoryId + id,
                                    name
                                })
                            }>
                            <SubCategoryTitle>{name}</SubCategoryTitle>
                        </SubCategoryTab>
                    )
                })}
            </Row>
            <Line />
        </SubCategoriesTabs>
    )

    renderTilesRow = items => {
        const { setCurrentItemInfos } = this.props
        const { width } = this.state
        const isSmallScreen = width < breakpoints.small
        const isMobileScreen = width < breakpoints.medium
        const isMediumScreen = width < breakpoints.large
        const contentSize = isMediumScreen ? width : 1280
        const TILE_SIZE = isSmallScreen
            ? (contentSize - 80) / 2
            : isMobileScreen
            ? (contentSize - 20 - 80) / 3
            : (contentSize - 140) / 4

        return (
            <TilesRow>
                {items.map((displayableProduct: DisplayableProduct, index) => {
                    const {
                        id,
                        categoryId,
                        subCategoryId,
                        image,
                        hasSelectedAllergens,
                        price,
                        ...rest
                    } = displayableProduct

                    const key = `${categoryId}-${subCategoryId ||
                        'no-subcategory'}-${id}-${index}`

                    return (
                        <DisplayableProductTile
                            {...rest}
                            key={key}
                            id={id}
                            price={price}
                            imageUrl={image}
                            hasAllergens={hasSelectedAllergens}
                            IconTopRight={null}
                            currentSelectedOnStore={setCurrentItemInfos}
                            categoryId={categoryId}
                            style={{
                                width: TILE_SIZE,
                                height: TILE_SIZE
                            }}
                        />
                    )
                })}
            </TilesRow>
        )
    }

    getRefPosition = (ref: any) => {
        const { width } = this.state
        const scrollSpyOffset = width < breakpoints.medium ? 65 : 95
        const headerOffset = width < breakpoints.medium ? 60 : 100
        return (
            ref &&
            ref.current &&
            ref.current.offsetTop - headerOffset - scrollSpyOffset
        )
    }

    scrollToMyRef = (ref: any) => {
        const refPosition = this.getRefPosition(ref)
        window.scrollTo(0, refPosition)
    }

    onHeaderTabClick = (tab: { id: string, name: string }) => {
        const { id, name } = tab
        this.props.pageMetadata({ title: name })
        this.scrollToMyRef(this.categoryRefs[id])
    }

    onSubCategoryTabClick = (tab: { id: string, name: string }) => {
        const { sendMeta } = this.props
        const { id, name } = tab
        sendMeta({
            ...eventsDefinitions.ECOMM_CATALOG_FILTER,
            event_label: name
        })
        sendMeta({
            ...eventsDefinitions.GA4_ECOMM_CATALOG_FILTER,
            filter_name: name
        })
        this.scrollToMyRef(this.subCategoriesRefs[id])
    }

    render() {
        const {
            catalog,
            priceTotal,
            withoutBacon,
            toggleWithoutBacon,
            allergens,
            selectAllergens,
            selectedAllergens,
            restaurant,
            navigate,
            intl,
            coupon
        } = this.props

        const { id: restaurantId } = restaurant
        const { currentCategoryId, showCouponModal, catalogError } = this.state
        const headerTabs = catalog.reduce((acc, category) => {
            const { id, name } = category
            category.name && acc.push({ id, name })
            return acc
        }, [])

        return (
            <>
                <Layout
                    background="plain"
                    border
                    backEnabled
                    closeEnabled
                    backUrl={
                        this.props.collectMode === orderPickUpType.TABLE_ORDER
                            ? undefined
                            : intl.formatMessage({
                                  id: 'routes.clickandcollect.delivery.choice'
                              })
                    }>
                    {catalogError && (
                        <AlertModal
                            isOpen={catalogError}
                            alertTitleKey="oops"
                            alertContent={intl.formatMessage(
                                {
                                    id:
                                        'clickandcollect.search.restaurant.error.fetch.catalog'
                                },
                                {
                                    resto: restaurant.name
                                }
                            )}
                            leftLabel="app.error.button.cancel"
                            okLabel="app.error.button.retry"
                            handleClose={() => {
                                this.setState({ catalogError: false })
                            }}
                            onLeftClick={() => {
                                this.setState({ catalogError: false })
                                navigate(
                                    intl.formatMessage({
                                        id:
                                            'routes.clickandcollect.restaurant.choice'
                                    })
                                )
                            }}
                            onOkClick={() =>
                                this.retryFetchCatalog(restaurantId)
                            }
                        />
                    )}
                    <Container>
                        <CatalogHeader
                            tabs={headerTabs}
                            onTabClick={this.onHeaderTabClick}
                            currentTabId={currentCategoryId}
                        />
                        <CouponModal
                            isOpen={showCouponModal}
                            handleClose={() => {
                                this.toggleCouponModal(false)
                            }}
                        />
                        <Content>
                            <ResponsiveContainer>
                                <ImpersonalOperationCard
                                    onClick={() => this.toggleCouponModal(true)}
                                />
                                <CatalogAllergens
                                    withoutBacon={withoutBacon}
                                    toggleWithoutBacon={toggleWithoutBacon}
                                    selectAllergens={selectAllergens}
                                    actualSelectedAllergens={selectedAllergens}
                                    allergens={allergens}
                                />
                                {catalog.map((item, index) =>
                                    this.renderSection(item, index)
                                )}
                            </ResponsiveContainer>
                        </Content>
                    </Container>
                    <CartFooterContainer>
                        <CartFooter price={priceTotal} />
                    </CartFooterContainer>
                    {/* TODO: feature toggle */}
                    <FeatureToggle
                        feature={featureCodes.TABLE_ORDER_FREE_HOT_DRINK}>
                        <FreeHotDrinkModal
                            restaurantId={restaurantId}
                            visible={this.state.displayFreeHotDrinkModal}
                            onClose={() =>
                                this.setState({
                                    ...this.state,
                                    displayFreeHotDrinkModal: false
                                })
                            }
                        />
                    </FeatureToggle>
                </Layout>
                {coupon && <NextPromotionScreenHandler coupon={coupon} />}
            </>
        )
    }
}

const Container = styled.div`
    margin-top: 50px;
    ${media.medium`
        margin-top: 90px;
    `};
`

const Content = styled.div`
    background-color: ${tertiary};
    overflow: auto;
    padding-bottom: 100px;
`

const Row = styled.div`
    display: flex;
    flex-direction: row;
`

const CategoryTitle = styled.div`
    margin-top: 25px;
    margin-bottom: 10px;
`

const SubCategoryTitle = styled(H6)`
    padding: ${SPACING.XS} ${SPACING.S} ${SPACING.XS} ${SPACING.XS};
    ${media.medium`
        padding-right: 50px
    `}
`

const TilesRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    ${media.medium`
        padding: 10px;
    `}
`

const Line = styled.div`
    height: 1px;
    background: ${colors.lightGrey};
    width: 100%;
    ${media.medium`
        width: 80%;
    `}
`

const SubCategoriesTabs = styled.div`
    ${media.medium`
        margin: 20px 0;
    `}
`

const SubCategoryTab = styled.div`
    cursor: pointer;
`

const CartFooterContainer = styled.div`
    position: fixed;
    width: 100%;
    bottom: 0;
    z-index: 9999;
`

export const CatalogComponent = injectIntl(Catalog)
